/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { type ContentStackIconTitleTextInfo } from '../../interface';
import { CMSHeadline } from '../CMSHeadline/CMSHeadline';
import NextImage, { blurDataURLPlaceholder } from '@/components/NextImage';
import { Icon, Text } from '@az/starc-ui';
import { ArrowRight } from '@az/starc-ui-icons';
import { FlexGrid } from '@/components/FlexGrid/FlexGrid';
import { CMSRichText } from '../CMSRichText/CMSRichText';
import { CMSButton } from '../CMSButton/CMSButton';
import { CMSLink } from '../CMSLink/CMSLink';
import styles from './CMSIconTitleTextInfo.module.scss';
import cx from 'classnames';

export type CMSIconTitleTextInfoProps = {
  content: ContentStackIconTitleTextInfo;
};

export function CMSIconTitleTextInfo({ content }: CMSIconTitleTextInfoProps) {
  const { headline, layout, alignment, outline, grid_content, link } = content;

  const threeColumnLayout = layout === 'Three';
  const fourColumnLayout = layout === 'Four';
  const centerAlignment = alignment === 'Center';

  const columns = fourColumnLayout ? 4 : threeColumnLayout ? 3 : 2;

  return (
    <div>
      {headline ? <CMSHeadline>{headline}</CMSHeadline> : null}
      <div
        data-testid="icon-title-text-info-grid"
        className={styles.iconTitleInfoGrid}
        style={{ '--az-cms-icon-title-text-info-grid-columns': columns } as React.CSSProperties}
      >
        {grid_content?.map(({ icon, title, rich_text_editor, link, buttons }, i) => {
          return (
            <div
              key={i}
              data-testid="grid-item"
              className={cx(styles.iconTitleInfoGridItem, {
                [styles.outlined]: outline,
              })}
            >
              <FlexGrid
                flexDirection="column"
                gap={3}
                alignItems={centerAlignment ? 'center' : undefined}
              >
                <FlexGrid alignItems="center" gap={3}>
                  <NextImage
                    className={styles.icon}
                    alt=""
                    src={icon ?? blurDataURLPlaceholder}
                    width={60}
                    height={60}
                  />
                  <Text
                    weight="bold"
                    attributes={{
                      'data-testid': 'grid-item-title',
                    }}
                    className={styles.title}
                  >
                    {title}
                  </Text>
                </FlexGrid>
                {rich_text_editor ? (
                  <CMSRichText
                    content={{
                      title: title,
                      description: rich_text_editor,
                      textAlignment: alignment,
                    }}
                    fullWidth
                  />
                ) : null}
                {link?.title && link.href ? (
                  <CMSLink to={link.href}>
                    <FlexGrid as="span" gap={2} alignItems="center">
                      <Text as="span" decoration="underline" className={styles.linkTitle}>
                        {link.title}
                        <span className="sr-only">{title}</span>
                      </Text>
                      <Icon svg={ArrowRight} size={6} color="accent" />
                    </FlexGrid>
                  </CMSLink>
                ) : null}
                {buttons?.length ? (
                  <FlexGrid
                    gap={4}
                    flexWrap="wrap"
                    justifyContent={centerAlignment ? 'center' : undefined}
                  >
                    {buttons.map((button, i) => (
                      <CMSButton key={i} options={button} />
                    ))}
                  </FlexGrid>
                ) : null}
              </FlexGrid>
            </div>
          );
        })}
      </div>
      {link?.href && link.title ? (
        <FlexGrid justifyContent="flex-end" className={styles.gridFooter}>
          <CMSLink to={link.href}>
            <FlexGrid gap={2} alignItems="center">
              <Text weight="medium" variant="main-body" size="100">
                {link.title}
              </Text>
              <Icon svg={ArrowRight} size={6} color="accent" />
            </FlexGrid>
          </CMSLink>
        </FlexGrid>
      ) : null}
    </div>
  );
}
